import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, X, Check, ChevronDown, MessageCircle } from "lucide-react";

// ============================================================
// REPLACE THESE TWO BEFORE GOING LIVE
// ============================================================
const BOOKING_URL = "/book";
const WHATSAPP_URL = "https://wa.me/918979106414";

// Fire Meta Pixel "Lead" + GA "generate_lead" when any "Book a Call" CTA is clicked.
const trackBookCta = (cta) => {
  try {
    if (window.fbq) window.fbq("track", "Lead", { content_name: "CoachesLanding", source: cta });
    if (window.gtag) window.gtag("event", "generate_lead", { value: 1, currency: "INR", source: cta });
  } catch (e) {
    // swallow
  }
};

// ----- Chili pepper watermark SVG -----
const ChiliWatermark = ({ className = "", size = 480 }) => (
  <svg
    viewBox="0 0 200 200"
    className={className}
    width={size}
    height={size}
    aria-hidden="true"
  >
    <path
      d="M50 30 C65 25, 80 30, 88 42 C95 36, 108 38, 115 50 C108 50, 100 55, 96 62 C112 70, 130 90, 138 120 C145 148, 130 168, 105 175 C82 181, 58 168, 50 142 C44 122, 50 100, 62 84 C56 76, 50 64, 50 54 C50 46, 50 38, 50 30 Z"
      fill="currentColor"
    />
  </svg>
);

// ----- Sub-component: animated reveal -----
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.25 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

// ----- Sticky Header -----
const Header = () => (
  <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/5">
    <div className="max-w-7xl mx-auto px-4 md:px-8 h-52 md:h-72 flex items-center justify-between">
      <a href="#top" className="flex items-center" data-testid="coaches-logo">
        <img src="/assets/logo.png" alt="BrandChilly" className="h-48 md:h-64 w-auto" />
      </a>
      <a
        href={BOOKING_URL}
        onClick={() => trackBookCta("header")}
        className="c-btn-primary text-xs md:text-sm py-2.5 px-4 md:px-5"
        data-testid="header-book-btn"
      >
        Book Free Call <ArrowRight size={14} />
      </a>
    </div>
  </header>
);

// ----- Section 1: HERO -----
const Hero = () => (
  <section id="top" className="relative overflow-hidden pt-10 pb-16 md:pt-20 md:pb-24" style={{ background: "var(--c-bg)" }}>
    <div className="c-watermark text-white" style={{ top: -80, right: -120 }}>
      <ChiliWatermark size={520} />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <div className="c-eyebrow mb-5">For Coaches & Consultants</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h1 className="text-[40px] leading-[1.04] sm:text-5xl md:text-6xl lg:text-7xl">
            You Know How To Get Results For Clients. You Just Don't Have A{" "}
            <span className="c-red italic">System To Get The Clients.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 md:mt-8 max-w-xl text-base md:text-lg leading-relaxed c-muted">
            In one free 30-minute Growth Strategy Call, I'll hand you the exact
            Marketing & Sales System I've built over 11 years and 3,000+ trained
            professionals — explained in plain language, with zero jargon, and
            simple enough that you can run it yourself and see whether it's
            working within days.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <a
            href={BOOKING_URL}
            onClick={() => trackBookCta("hero")}
            className="c-btn-primary mt-8 text-base md:text-base"
            data-testid="hero-book-btn"
          >
            Book My Free Strategy Call <ArrowRight size={18} />
          </a>
          <p className="mt-3 text-xs md:text-sm c-muted">
            30 minutes · Online · No pitch, just strategy · Free for the first 5 coaches this month
          </p>
        </Reveal>
        <Reveal delay={0.28}>
          <div className="mt-10 pt-6 border-t border-white/5 text-xs md:text-sm c-muted tracking-wide">
            11+ Years in Marketing · 3,000+ Professionals Trained · Ex-CMO · Background: IBM · Wipro · HCL
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="lg:col-span-5">
        <div className="relative">
          <img
            src="/assets/founder.jpg"
            alt="Saurabh Khaspuri — Founder, BrandChilly"
            className="w-full h-auto"
            style={{ filter: "grayscale(100%) contrast(1.08) brightness(0.9)", borderRadius: 0 }}
          />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(10,10,10,0.65) 100%)" }} />
        </div>
      </Reveal>
    </div>
  </section>
);

// ----- Section 2: THE REAL PROBLEM -----
const Problem = () => {
  const points = [
    "Chasing prospects in the DMs and hoping they reply",
    "Dropping your price just to close someone who's on the fence",
    "Posting content that earns likes but never a single inbound lead",
    "No real brand, no authority, no predictable pipeline",
  ];
  return (
    <section className="relative overflow-hidden py-16 md:py-28" style={{ background: "var(--c-bg-alt)" }}>
      <div className="c-watermark text-white" style={{ bottom: -100, left: -120 }}>
        <ChiliWatermark size={460} />
      </div>
      <div className="relative max-w-5xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="c-eyebrow mb-5">The Real Problem</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl leading-[1.05]">
            It's Not Your Service. <span className="c-red italic">It's Your System.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 md:mt-8 text-base md:text-lg c-muted leading-relaxed max-w-3xl">
            You're genuinely good at what you do. Your clients get real results.
            But great work has never sold itself — and right now, getting your
            next client probably looks like this:
          </p>
        </Reveal>

        <ul className="mt-9 md:mt-12 space-y-4 md:space-y-5 max-w-3xl">
          {points.map((p, i) => (
            <Reveal key={i} delay={0.15 + i * 0.06}>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-6 h-6 border border-red-700/40 c-red flex-shrink-0">
                  <X size={14} strokeWidth={2.4} />
                </span>
                <span className="text-base md:text-lg text-white">{p}</span>
              </li>
            </Reveal>
          ))}
        </ul>

        <Reveal delay={0.5}>
          <p className="mt-10 md:mt-14 text-lg md:text-xl leading-relaxed max-w-3xl text-white">
            Here's the truth most coaches never hear: you don't have a talent
            problem, and you don't have an effort problem. You have a{" "}
            <span className="c-red italic">system</span> problem. And unlike
            talent, a system can be built — fast.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

// ----- Section 3: IS THIS YOU? -----
const IsThisYou = () => {
  const items = [
    "You're a coach or consultant with real, proven expertise",
    "You struggle to get consistent, good-quality clients",
    "You've tried posting, ads, and referrals — nothing sticks",
    "You know you need a system, but you don't know where to start",
  ];
  return (
    <section className="relative overflow-hidden py-16 md:py-28" style={{ background: "var(--c-bg)" }}>
      <div className="relative max-w-5xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="c-eyebrow mb-5">Stop. Read This.</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl leading-[1.05]">
            Is This <span className="c-red italic">You?</span>
          </h2>
        </Reveal>

        <ul className="mt-9 md:mt-12 space-y-4 md:space-y-5 max-w-3xl">
          {items.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.06}>
              <li className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-6 h-6 border border-red-700/40 c-red flex-shrink-0">
                  <Check size={14} strokeWidth={2.4} />
                </span>
                <span className="text-base md:text-lg text-white">{p}</span>
              </li>
            </Reveal>
          ))}
        </ul>

        <Reveal delay={0.4}>
          <p className="mt-10 md:mt-14 text-lg md:text-xl text-white max-w-3xl">
            If even two of these are true, this 30-minute call was built for you.
          </p>
        </Reveal>
        <Reveal delay={0.5}>
          <a
            href={BOOKING_URL}
            onClick={() => trackBookCta("is_this_you")}
            className="c-btn-primary mt-8"
            data-testid="is-this-you-cta"
          >
            Yes, This Is Me — Book The Call <ArrowRight size={18} />
          </a>
        </Reveal>
      </div>
    </section>
  );
};

// ----- Section 4: WHAT WE COVER -----
const WhatWeCover = () => {
  const cards = [
    {
      n: "01",
      title: "Your Brand Audit",
      body: "A clear, honest look at where you stand today — what's working, what's broken, and the single biggest gap holding your growth back right now.",
    },
    {
      n: "02",
      title: "Your Personal Branding Blueprint",
      body: "How to build a brand that carries authority and attracts the right clients — not just followers who never buy.",
    },
    {
      n: "03",
      title: "Your AI Lead Generation System",
      body: "The exact method to turn simple ads and a clear funnel into real, qualified leads — explained step by step, in plain language, so you can actually run it.",
    },
    {
      n: "04",
      title: "Your 30-Day Action Plan",
      body: "You leave the call with a personalised roadmap you can execute yourself — clear steps, no jargon, no guesswork, and a simple way to check if it's actually working.",
    },
  ];
  return (
    <section className="relative overflow-hidden py-16 md:py-28" style={{ background: "var(--c-bg-alt)" }}>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="c-eyebrow mb-5">Here's Exactly What We Cover</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl leading-[1.05] max-w-4xl">
            30 Minutes. Four Things That Change How You Get Clients.
          </h2>
        </Reveal>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {cards.map((c, i) => (
            <Reveal key={c.n} delay={0.1 + i * 0.07}>
              <div className="c-card h-full" style={{ borderTop: "2px solid var(--c-red)" }}>
                <div className="text-4xl md:text-5xl mb-4 c-red" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 700 }}>
                  {c.n}
                </div>
                <h3 className="text-2xl md:text-3xl mb-3">{c.title}</h3>
                <p className="c-muted leading-relaxed text-sm md:text-base">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- Section 5: WHO YOU'RE TALKING TO -----
const WhoYouTalkTo = () => {
  const chips = [
    "11+ Years in Marketing",
    "3,000+ Trained",
    "Ex-CMO",
    "IBM / Wipro / HCL",
    "Founder, BrandChilly",
  ];
  return (
    <section className="relative overflow-hidden py-16 md:py-28" style={{ background: "var(--c-bg)" }}>
      <div className="c-watermark text-white" style={{ top: -60, right: -100 }}>
        <ChiliWatermark size={420} />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <img
            src="/assets/founder.jpg"
            alt="Saurabh Khaspuri"
            className="w-full h-auto"
            style={{ filter: "grayscale(100%) contrast(1.08) brightness(0.9)", borderRadius: 0 }}
          />
        </Reveal>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <div className="c-eyebrow mb-5">Who's Behind This</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-4xl md:text-6xl leading-[1.05]">
              11 Years. The Same Pattern. <span className="c-red italic">Always Fixable.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-6 md:mt-8 space-y-5 c-muted text-base md:text-lg leading-relaxed">
              <p>
                I'm Saurabh Khaspuri — founder of BrandChilly, digital
                marketing strategist, and trainer to over 3,000 professionals.
                My career spans IBM, Wipro, and HCL, and I've served as a CMO
                building brands and lead-generation systems from the ground up.
              </p>
              <p>
                In 11 years, I've seen the same pattern in almost every coach
                and consultant I meet: excellent service, broken marketing
                system. It's never the talent. It's always the system — and a
                broken system is one of the easiest things in the world to fix,
                once someone shows you where it's leaking. That's the entire
                point of this call.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap gap-2">
              {chips.map((c) => (
                <span key={c} className="c-chip">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

// ----- Section 6: FINAL CTA + FAQ -----
const FAQS = [
  {
    q: "Is this really free?",
    a: "Yes. The first 5 strategy calls each month are completely free. No card, no catch.",
  },
  {
    q: "Will you try to sell me something?",
    a: "No. If it makes sense to work together, I'll show you how — as an invitation, never a pitch. The strategy is yours to keep either way.",
  },
  {
    q: "Who is this for?",
    a: "Coaches and consultants who are great at their craft but don't yet have a predictable, repeatable system for getting clients.",
  },
];

const FAQItem = ({ q, a, isOpen, onToggle, index }) => (
  <div className="border-t border-white/5">
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 py-5 md:py-6 text-left"
      data-testid={`coaches-faq-${index}`}
    >
      <span className="text-base md:text-lg text-white">{q}</span>
      <ChevronDown
        size={20}
        className={`flex-shrink-0 mt-1 c-red transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
      />
    </button>
    <div
      className={`grid transition-all duration-500 ease-out ${
        isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
      }`}
    >
      <div className="overflow-hidden">
        <p className="c-muted text-sm md:text-base leading-relaxed">{a}</p>
      </div>
    </div>
  </div>
);

const FinalCTA = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="relative overflow-hidden py-16 md:py-28" style={{ background: "var(--c-bg-alt)" }}>
      <div className="c-watermark text-white" style={{ top: -120, left: "50%", transform: "translateX(-50%)" }}>
        <ChiliWatermark size={560} />
      </div>
      <div className="relative max-w-4xl mx-auto px-5 md:px-8 text-center">
        <Reveal>
          <div className="c-eyebrow mb-5">One Call. Zero Risk.</div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-4xl md:text-6xl leading-[1.05]">
            Book Your Free <span className="c-red italic">Growth Strategy Call</span>
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 md:mt-8 c-muted text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            This is a genuine 30-minute strategy call — no pitch, no hard sell.
            You'll walk away with a clear picture of what's broken and a
            concrete plan to fix it, whether we ever work together or not. I'm
            keeping this open to only 5 coaches this month, so every call gets
            real depth and a real plan.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <a
            href={BOOKING_URL}
            onClick={() => trackBookCta("final")}
            className="c-btn-primary mt-9 text-base md:text-lg px-8 py-4"
            data-testid="final-book-btn"
          >
            Book My Free Strategy Call <ArrowRight size={20} />
          </a>
          <p className="mt-3 text-xs md:text-sm c-muted">
            30 minutes · Online · Free for the first 5 coaches · Limited slots remaining
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="mt-14 md:mt-20 max-w-2xl mx-auto text-left">
            <div className="c-eyebrow mb-6 text-center">Quick Questions</div>
            <div className="border-b border-white/5">
              {FAQS.map((f, i) => (
                <FAQItem
                  key={i}
                  index={i}
                  q={f.q}
                  a={f.a}
                  isOpen={open === i}
                  onToggle={() => setOpen(open === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ----- Footer -----
const Footer = () => (
  <footer className="py-10 md:py-12 border-t border-white/5" style={{ background: "var(--c-bg)" }}>
    <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div className="flex flex-col gap-3">
        <img src="/assets/logo.png" alt="BrandChilly" className="h-24 md:h-28 w-auto self-start" />
        <p className="c-muted text-sm" style={{ fontFamily: "Cormorant Garamond, serif", fontStyle: "italic" }}>
          Spice Up Your Brand With Advertising
        </p>
      </div>
      <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-white hover:c-red transition"
          data-testid="footer-whatsapp"
        >
          <MessageCircle size={15} className="c-red" /> Chat on WhatsApp
        </a>
        <span className="c-muted text-xs">© BrandChilly</span>
      </div>
    </div>
  </footer>
);

// ----- PAGE -----
export default function CoachesLandingPage() {
  useEffect(() => {
    document.title = "BrandChilly — Free Growth Strategy Call for Coaches";
    const setMeta = (name, content, attr = "name") => {
      let tag = document.querySelector(`meta[${attr}='${name}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("description", "Free 30-minute Growth Strategy Call for coaches & consultants. Built on 11 years and 3,000+ trained professionals. No pitch — just the system to get the clients.");
    setMeta("og:title", "BrandChilly — Free Growth Strategy Call for Coaches", "property");
    setMeta("og:description", "30 minutes. Zero pitch. A clear plan to fix your client-acquisition system. Free for the first 5 coaches this month.", "property");

    // Meta Pixel + Google Ads: fire ViewContent on page load for retargeting
    try {
      if (window.fbq) window.fbq("track", "ViewContent", { content_name: "CoachesLanding", content_category: "Lead Gen Landing" });
      if (window.gtag) window.gtag("event", "view_content", { content_name: "CoachesLanding" });
    } catch (e) {
      // swallow
    }
  }, []);

  return (
    <div className="coaches-page">
      <Header />
      <Hero />
      <Problem />
      <IsThisYou />
      <WhatWeCover />
      <WhoYouTalkTo />
      <FinalCTA />
      <Footer />
    </div>
  );
}
